import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Header from "@/components/layout/header";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  RefreshCw,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  Eye,
  AlertTriangle,
  Database,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { MonitoringLayout } from "@/pages/monitoring/layout";

// Types
interface CacheHealth {
  status: 'healthy' | 'unhealthy';
  timestamp: string;
  cache: {
    connected: boolean;
    [key: string]: any;
  };
}

interface CacheKey {
  key: string;
  value: string | null;
}

interface CacheKeysResponse {
  pattern: string;
  totalKeys: number;
  showing: number;
  keys: CacheKey[];
  timestamp: string;
}

interface ClearRunningFlagsResponse {
  success: boolean;
  message: string;
  cleared: { key: string; value: string | null }[];
  timestamp: string;
}

interface DeleteKeysResponse {
  success: boolean;
  message: string;
  deleted: { key: string; existed: boolean }[];
  timestamp: string;
}

interface ClearSyncProgressResponse {
  success: boolean;
  message: string;
  totalFound: number;
  deleted: number;
  timestamp: string;
}

// Predefined patterns for quick filtering
const QUICK_PATTERNS = [
  { label: "All Keys", pattern: "*" },
  { label: "SQL Sync Status", pattern: "sqlsync:*:status*" },
  { label: "SQL Sync Running", pattern: "sqlsync:*:running" },
  { label: "SQL Sync Progress", pattern: "pl:sql-sync-*" },
  { label: "Field Sync Running", pattern: "field-sync:running:*" },
  { label: "Rate Limits", pattern: "*rate*" },
  { label: "Dashboard", pattern: "*dashboard*" },
];

export default function CacheManagementPage() {
  const [searchPattern, setSearchPattern] = useState("*");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClearRunningConfirm, setShowClearRunningConfirm] = useState(false);
  const [showClearProgressConfirm, setShowClearProgressConfirm] = useState(false);
  const [viewingValue, setViewingValue] = useState<{ key: string; value: string } | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch cache health
  const { data: health, isLoading: healthLoading, refetch: refetchHealth } = useQuery<CacheHealth>({
    queryKey: ["cache-health"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/cache/health");
      if (!response.ok) throw new Error("Failed to fetch cache health");
      return response.json();
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch cache keys
  const { data: keysData, isLoading: keysLoading, refetch: refetchKeys } = useQuery<CacheKeysResponse>({
    queryKey: ["cache-keys", searchPattern],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/cache/keys?pattern=${encodeURIComponent(searchPattern)}`);
      if (!response.ok) throw new Error("Failed to fetch cache keys");
      return response.json();
    },
  });

  // Clear running flags mutation
  const clearRunningMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", "/api/cache/running-flags");
      if (!response.ok) throw new Error("Failed to clear running flags");
      return response.json() as Promise<ClearRunningFlagsResponse>;
    },
    onSuccess: (data) => {
      toast({
        title: "Running Flags Cleared",
        description: data.message,
      });
      queryClient.invalidateQueries({ queryKey: ["cache-keys"] });
      setShowClearRunningConfirm(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete specific keys mutation
  const deleteKeysMutation = useMutation({
    mutationFn: async (keys: string[]) => {
      const response = await apiRequest("DELETE", "/api/cache/keys", { keys });
      if (!response.ok) throw new Error("Failed to delete cache keys");
      return response.json() as Promise<DeleteKeysResponse>;
    },
    onSuccess: (data) => {
      toast({
        title: "Keys Deleted",
        description: data.message,
      });
      setSelectedKeys(new Set());
      queryClient.invalidateQueries({ queryKey: ["cache-keys"] });
      setShowDeleteConfirm(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Clear sync progress keys mutation
  const clearProgressMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", "/api/cache/sync-progress");
      if (!response.ok) throw new Error("Failed to clear sync progress keys");
      return response.json() as Promise<ClearSyncProgressResponse>;
    },
    onSuccess: (data) => {
      toast({
        title: "Progress Keys Cleared",
        description: data.message,
      });
      queryClient.invalidateQueries({ queryKey: ["cache-keys"] });
      setShowClearProgressConfirm(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Handle select all
  const handleSelectAll = () => {
    if (!keysData?.keys) return;
    if (selectedKeys.size === keysData.keys.length) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(keysData.keys.map((k) => k.key)));
    }
  };

  // Handle individual select
  const handleSelectKey = (key: string) => {
    const newSelected = new Set(selectedKeys);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedKeys(newSelected);
  };

  // Format value for display
  const formatValue = (value: string | null | unknown): string => {
    if (value === null || value === undefined) return "(null)";
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return value;
      }
    }
    // If it's already an object, stringify it
    return JSON.stringify(value, null, 2);
  };

  // Truncate value for table display
  const truncateValue = (value: string | null | unknown, maxLength = 50): string => {
    if (value === null || value === undefined) return "(null)";
    // Convert to string if not already
    const strValue = typeof value === "string" ? value : JSON.stringify(value);
    if (strValue.length <= maxLength) return strValue;
    return strValue.substring(0, maxLength) + "...";
  };

  // Get key category for badge coloring
  const getKeyCategory = (key: string): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } => {
    if (key.includes(":running")) return { label: "Running", variant: "destructive" };
    if (key.includes(":status")) return { label: "Status", variant: "default" };
    if (key.includes("dashboard")) return { label: "Dashboard", variant: "secondary" };
    if (key.includes("rate")) return { label: "Rate Limit", variant: "outline" };
    return { label: "Other", variant: "outline" };
  };

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view Cache Management</p>
        </div>
      </div>
    }>
    <MonitoringLayout>
    <div className="flex flex-col min-h-screen">
      <Header title="Cache Management" />

      <div className="flex-1 p-6 space-y-6">
        {/* Health Status Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="h-5 w-5" />
                Redis Cache Status
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchHealth()}
                disabled={healthLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${healthLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {healthLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : health ? (
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  {health.cache.connected ? (
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-500" />
                  )}
                  <span className="text-lg font-medium">
                    {health.cache.connected ? "Connected" : "Disconnected"}
                  </span>
                </div>
                <Badge variant={health.status === "healthy" ? "default" : "destructive"}>
                  {health.status}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Last checked: {new Date(health.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ) : (
              <p className="text-muted-foreground">Unable to fetch cache status</p>
            )}
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-4 flex-wrap">
            <Button
              variant="destructive"
              onClick={() => setShowClearRunningConfirm(true)}
              disabled={clearRunningMutation.isPending}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Clear All Running Flags
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowClearProgressConfirm(true)}
              disabled={clearProgressMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Old Sync Progress Keys
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                refetchKeys();
                refetchHealth();
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh All
            </Button>
          </CardContent>
        </Card>

        {/* Keys Browser Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Cache Keys Browser</CardTitle>
              {selectedKeys.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleteKeysMutation.isPending}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedKeys.size})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search and Quick Filters */}
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter pattern (e.g., sqlsync:*)"
                    value={searchPattern}
                    onChange={(e) => setSearchPattern(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && refetchKeys()}
                  />
                  <Button onClick={() => refetchKeys()} disabled={keysLoading}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Pattern Buttons */}
            <div className="flex gap-2 flex-wrap">
              {QUICK_PATTERNS.map((p) => (
                <Button
                  key={p.pattern}
                  variant={searchPattern === p.pattern ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSearchPattern(p.pattern)}
                >
                  {p.label}
                </Button>
              ))}
            </div>

            {/* Results Info */}
            {keysData && (
              <div className="text-sm text-muted-foreground">
                Showing {keysData.showing} of {keysData.totalKeys} keys matching "{keysData.pattern}"
              </div>
            )}

            {/* Keys Table */}
            {keysLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : keysData && keysData.keys.length > 0 ? (
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedKeys.size === keysData.keys.length && keysData.keys.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Key</TableHead>
                      <TableHead className="w-24">Category</TableHead>
                      <TableHead>Value (Preview)</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keysData.keys.map((item) => {
                      const category = getKeyCategory(item.key);
                      return (
                        <TableRow key={item.key}>
                          <TableCell>
                            <Checkbox
                              checked={selectedKeys.has(item.key)}
                              onCheckedChange={() => handleSelectKey(item.key)}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {item.key}
                          </TableCell>
                          <TableCell>
                            <Badge variant={category.variant}>{category.label}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground max-w-md truncate">
                            {truncateValue(item.value)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewingValue({ key: item.key, value: item.value || "(null)" })}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No keys found matching pattern "{searchPattern}"
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Clear Running Flags Confirmation Dialog */}
      <AlertDialog open={showClearRunningConfirm} onOpenChange={setShowClearRunningConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Running Flags?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This will clear current and legacy SQL/field sync running and pause keys from the cache, including
                  <code className="bg-muted px-1 rounded text-xs ml-1">pl:sqlsync:entity:*:running</code> and related field sync keys.
                  Use this when tasks appear stuck in "running" state but are actually not running.
                </p>
                <p>The dashboard cache will also be cleared to refresh the UI.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => clearRunningMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {clearRunningMutation.isPending ? "Clearing..." : "Clear Running Flags"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Keys Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedKeys.size} Cache Keys?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>This action cannot be undone. The following keys will be deleted:</p>
                <div className="mt-2 max-h-40 overflow-y-auto bg-muted p-2 rounded text-xs font-mono">
                  {Array.from(selectedKeys).map((key) => (
                    <div key={key}>{key}</div>
                  ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteKeysMutation.mutate(Array.from(selectedKeys))}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteKeysMutation.isPending ? "Deleting..." : `Delete ${selectedKeys.size} Keys`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Sync Progress Keys Confirmation Dialog */}
      <AlertDialog open={showClearProgressConfirm} onOpenChange={setShowClearProgressConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Old Sync Progress Keys?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This will delete all <code className="bg-muted px-1 rounded text-xs">pl:sql-sync-*</code> keys from the cache. These are
                  progress tracking keys from completed SQL sync jobs that accumulated because they
                  had no TTL set.
                </p>
                <p>
                  This operation may take a while if there are many keys (50k+). The page will
                  refresh automatically when complete.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => clearProgressMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {clearProgressMutation.isPending ? "Clearing..." : "Clear Progress Keys"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Value Dialog */}
      <Dialog open={!!viewingValue} onOpenChange={() => setViewingValue(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm break-all">
              {viewingValue?.key}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh]">
            <pre className="bg-muted p-4 rounded text-sm font-mono whitespace-pre-wrap break-all">
              {viewingValue ? formatValue(viewingValue.value) : ""}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </MonitoringLayout>
    </ViewGuard>
  );
}
